import React, { useEffect, useRef, useState } from "react";
import { Input } from "@fm/ui";
import { loadGoogleMapsAPI } from "../utils/googleMapsLoader";

// Declare Google Maps types for window object
declare global {
  interface Window {
    google: typeof google;
  }
}

interface StructuredAddress {
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  lat: number;
  lng: number;
}

interface GooglePlacesAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onPlaceSelect?: (place: StructuredAddress) => void;
  placeholder?: string;
  className?: string;
  apiKey: string;
}

const GooglePlacesAutocomplete: React.FC<GooglePlacesAutocompleteProps> = ({
  value,
  onChange,
  onPlaceSelect,
  placeholder = "Enter address...",
  className,
  apiKey,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const scrollPositionRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  // Load Google Maps API using shared loader
  useEffect(() => {
    loadGoogleMapsAPI(apiKey)
      .then(() => {
        setIsGoogleLoaded(true);
      })
      .catch((error) => {
        console.error("Error loading Google Maps API:", error);
      });
  }, [apiKey]);

  // Initialize autocomplete
  useEffect(() => {
    if (!isGoogleLoaded || !inputRef.current) return;

    // Create autocomplete with US-only restrictions
    autocompleteRef.current = new google.maps.places.Autocomplete(
      inputRef.current,
      {
        types: ["address"],
        componentRestrictions: { country: ["us"] },
        strictBounds: true,
        bounds: new google.maps.LatLngBounds(
          new google.maps.LatLng(24.396308, -125.0), // Southwest corner
          new google.maps.LatLng(49.384358, -66.93457), // Northeast corner
        ),
        fields: ["address_components", "formatted_address", "geometry"],
      },
    );

    let isProcessing = false;

    const tryGeocoderFallback = (address: string, eventSource: string) => {
      if (!window.google?.maps?.Geocoder) {
        return;
      }

      const geocoder = new google.maps.Geocoder();
      geocoder.geocode(
        {
          address: address,
          componentRestrictions: { country: "US" },
        },
        (results, status) => {
          if (status === "OK" && results && results[0]) {
            const result = results[0];

            // Convert geocoder result to place format and process
            const placeResult: google.maps.places.PlaceResult = {
              formatted_address: result.formatted_address,
              geometry: result.geometry,
              address_components: result.address_components,
            };

            processPlaceData(placeResult, `${eventSource}_geocoded`);
          } else {
            console.error("❌ Geocoding failed:", status);
          }
        },
      );
    };

    const processPlaceData = (
      place: google.maps.places.PlaceResult,
      eventSource: string,
    ) => {
      if (isProcessing) {
        return;
      }

      isProcessing = true;

      // Save current scroll position to prevent unwanted scrolling
      scrollPositionRef.current = {
        x: window.scrollX,
        y: window.scrollY,
      };

      // Verify it's a US address
      const countryComponent = place.address_components?.find((component) =>
        component.types.includes("country"),
      );

      if (countryComponent?.short_name === "US") {
        // Extract address components more robustly
        const addressComponents = place.address_components || [];

        const componentMap = new Map<
          string,
          google.maps.GeocoderAddressComponent
        >();
        addressComponents.forEach((c) => {
          c.types.forEach((t) => {
            if (!componentMap.has(t)) componentMap.set(t, c);
          });
        });

        const streetNumber = componentMap.get("street_number")?.long_name ?? "";
        const route = componentMap.get("route")?.long_name ?? "";
        const subpremise = componentMap.get("subpremise")?.long_name ?? "";

        const city =
          componentMap.get("locality")?.long_name ??
          componentMap.get("sublocality")?.long_name ??
          componentMap.get("sublocality_level_1")?.long_name ??
          componentMap.get("administrative_area_level_3")?.long_name ??
          componentMap.get("administrative_area_level_2")?.long_name ??
          "";

        const state =
          componentMap.get("administrative_area_level_1")?.short_name ?? "";

        const postal = componentMap.get("postal_code")?.long_name ?? "";
        const postalSuffix =
          componentMap.get("postal_code_suffix")?.long_name ?? "";
        const zipCode = postalSuffix ? `${postal}-${postalSuffix}` : postal;

        const formatted = place.formatted_address || "";
        const primaryAddress = [streetNumber, route].filter(Boolean).join(" ");
        const withUnit = subpremise
          ? `${primaryAddress} Apt ${subpremise}`
          : primaryAddress;
        const displayAddress = formatted || withUnit || primaryAddress;

        const lat = place.geometry?.location?.lat() ?? 0;
        const lng = place.geometry?.location?.lng() ?? 0;

        onChange(displayAddress);

        // Call onPlaceSelect callback
        if (onPlaceSelect) {
          onPlaceSelect({
            address: displayAddress,
            city,
            state,
            zipCode,
            country: "US",
            lat,
            lng,
          });
        }
      } else {
        alert("Please select a US address only.");
      }

      // Restore scroll position to prevent unwanted scrolling
      requestAnimationFrame(() => {
        window.scrollTo(
          scrollPositionRef.current.x,
          scrollPositionRef.current.y,
        );
      });

      // Reset processing flag after a delay
      setTimeout(() => {
        isProcessing = false;
      }, 500);
    };

    const processPlace = (eventSource: string) => {
      if (isProcessing) {
        return;
      }

      const place = autocompleteRef.current?.getPlace();

      if (!place || !place.address_components) {
        // Fallback: use geocoder with current input value
        const inputValue = inputRef.current?.value?.trim();
        if (inputValue && inputValue.length > 10) {
          // Reasonable address length
          tryGeocoderFallback(inputValue, eventSource);
        }
        return;
      }

      // Use the shared processing function
      processPlaceData(place, eventSource);
    };

    // Add multiple event listeners to catch selection
    const placeChangedListener = autocompleteRef.current.addListener(
      "place_changed",
      () => {
        processPlace("place_changed");
      },
    );

    // Add DOM event listeners to the input element for backup
    const inputElement = inputRef.current;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter" || e.key === "Tab") {
        setTimeout(() => processPlace(`keydown_${e.key}`), 100);
      }
    };

    const handleBlur = () => {
      // Save scroll position before processing
      scrollPositionRef.current = {
        x: window.scrollX,
        y: window.scrollY,
      };
      setTimeout(() => processPlace("blur"), 150);
    };

    const handleClick = () => {
      setTimeout(() => processPlace("click"), 100);
    };

    // Add DOM event listeners
    inputElement.addEventListener("keydown", handleKeyDown);
    inputElement.addEventListener("blur", handleBlur);
    inputElement.addEventListener("click", handleClick);

    return () => {
      if (placeChangedListener) {
        google.maps.event.removeListener(placeChangedListener);
      }

      // Clean up DOM event listeners
      if (inputElement) {
        inputElement.removeEventListener("keydown", handleKeyDown);
        inputElement.removeEventListener("blur", handleBlur);
        inputElement.removeEventListener("click", handleClick);
      }
    };
  }, [isGoogleLoaded, onChange, onPlaceSelect]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  return (
    <Input
      ref={inputRef}
      value={value}
      onChange={handleInputChange}
      placeholder={placeholder}
      className={className}
      autoComplete="off"
    />
  );
};

export default GooglePlacesAutocomplete;
